import {
  ActiveEvents,
  ChannelSchema,
  EventSchema,
  EventStateSchema,
  EventStatsSchema,
  InviteeSchema,
  PaginatedResponseSchema,
  PastEventSchema,
  PricingPlanSchema,
  UserRole,
  UserSchema
} from 'types';
import {
  CHANNEL_API_BASE_PATH,
  EVENT_API_BASE_PATH,
  PAGINATION_SIZE,
  PAYMENT_API_BASE_PATH,
  PRICING_PLAN_API_BASE_PATH,
  TOKEN_API_BASE_PATH,
  USER_API_BASE_PATH
} from 'utils';
import { api } from 'services';

const requests = {
  getRTCToken: (channelName: string, uid: number) =>
    api.instances.nonAuth.get<{ response: string }>(`${TOKEN_API_BASE_PATH}/rtc/${uid}/${channelName}`),

  getRTMToken: (uid: number) => api.instances.nonAuth.get<{ response: string }>(`${TOKEN_API_BASE_PATH}/rtm/${uid}`),

  getActiveEvents: () => api.instances.auth.get<ActiveEvents>(`${EVENT_API_BASE_PATH}/find/active`),

  getRecordedPastEvents: (page: number) =>
    api.instances.auth.get<PaginatedResponseSchema<PastEventSchema>>(`${EVENT_API_BASE_PATH}/find/recorded/events`, {
      params: {
        page,
        size: PAGINATION_SIZE
      }
    }),

  createEvent: (data: {
    eventName: string;
    hostName: string;
    enableRecording: boolean;
    membersOnly: boolean;
    hostAvatar?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    poster?: File;
  }) => {
    const { poster, ...event } = data;
    const formData = new FormData();

    // SOURCE: https://stackoverflow.com/a/24535293
    formData.append('event', new Blob([JSON.stringify(event)], { type: 'application/json' }));

    if (poster) formData.append('poster', poster);

    return api.instances.auth.post<EventSchema>(`${EVENT_API_BASE_PATH}/create`, formData);
  },

  updateScheduledEvent: (data: {
    code: string;
    name: string;
    memberOnly: boolean;
    enableRecording: boolean;
    description?: string;
    poster?: File;
  }) => {
    const { code: eventCode, poster, ...event } = data;
    const formData = new FormData();

    // SOURCE: https://stackoverflow.com/a/24535293
    formData.append('partialUpdate', new Blob([JSON.stringify(event)], { type: 'application/json' }));

    if (poster) formData.append('poster', poster);

    return api.instances.auth.patch<EventSchema>(`${EVENT_API_BASE_PATH}/${eventCode}`, formData);
  },

  startEvent: (eventCode: string) => api.instances.auth.post<EventSchema>(`${EVENT_API_BASE_PATH}/start/${eventCode}`),

  getEvent: (eventCode: string) => api.instances.auth.get<EventSchema>(`${EVENT_API_BASE_PATH}/find/${eventCode}`),

  deleteScheduledEvent: (eventCode: string) =>
    api.instances.auth.delete<EventSchema>(`${EVENT_API_BASE_PATH}/delete/scheduled/event/${eventCode}`),

  deletePastEvent: (eventCode: string) =>
    api.instances.auth.delete<EventSchema>(`${EVENT_API_BASE_PATH}/delete/past/event/${eventCode}`),

  createInvitee: (eventCode: string, name: string, moderator: boolean) =>
    api.instances.auth.post<InviteeSchema>(`${EVENT_API_BASE_PATH}/invitee/create`, {
      eventCode,
      name,
      role: moderator ? UserRole.MODERATOR : UserRole.SPEAKER
    }),

  removeInvitee: (eventCode: string, inviteeCode: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/${eventCode}/${inviteeCode}`),

  getInvitee: (eventCode: string, inviteeCode: string) =>
    api.instances.nonAuth.get<InviteeSchema>(`${EVENT_API_BASE_PATH}/${eventCode}/${inviteeCode}`),

  raiseHand: (eventCode: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/raise/hand/${eventCode}`),

  leaveEvent: (eventCode: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/leave/event/${eventCode}`),

  leaveStage: (eventCode: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/leave/stage/${eventCode}`),

  getInviteeToken: (eventCode: string, inviteeCode: string) =>
    api.instances.nonAuth.get<{ response: string }>(`${EVENT_API_BASE_PATH}/token/${eventCode}/${inviteeCode}`),

  joinAsViewer: (eventCode: string, viewer: { name: string; avatar: string }) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/join/as/viewer`, {
      eventCode,
      name: viewer.name,
      avatar: viewer.avatar
    }),

  declineInvitation: (eventCode: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/decline/invitation/${eventCode}`),

  joinAsSpeaker: (eventCode: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/join/as/speaker/${eventCode}`),

  joinAsInvitee: (eventCode: string, inviteeCode: string) =>
    api.instances.nonAuth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/join/as/invitee`, {
      eventCode,
      username: inviteeCode
    }),

  inviteViewer: (eventCode: string, username: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/invite/viewer`, {
      eventCode,
      username
    }),

  changeRole: (eventCode: string, username: string, role: UserRole) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/change/role`, {
      eventCode,
      username,
      newRole: role
    }),

  approveHandRaise: (eventCode: string, username: string) =>
    api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/approve/hand/raise`, {
      eventCode,
      username
    }),

  getLatestState: (eventCode: string) =>
    api.instances.auth.get<EventStateSchema>(`${EVENT_API_BASE_PATH}/latest/state/${eventCode}`),

  updateMaxViewerCount: (eventCode: string, maxViewerCount: number) =>
    api.instances.auth.put<{ maxViewerCount: number }>(`${EVENT_API_BASE_PATH}/update/max/viewer/count/${eventCode}`, {
      maxViewerCount
    }),

  incrementWatchCount: (eventCode: string, username: string) =>
    api.instances.auth.patch(`${EVENT_API_BASE_PATH}/update/increment-watch-count`, {
      eventCode,
      username
    }),

  removeFromStage: (eventCode: string, username: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/remove/from/stage`, {
      data: {
        eventCode,
        username
      }
    }),

  removePendingUser: (eventCode: string, username: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/remove/pending/user`, {
      data: {
        eventCode,
        username
      }
    }),

  removeHandRaiseRequest: (eventCode: string, username: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/remove/from/request/list`, {
      data: {
        eventCode,
        username
      }
    }),

  toggleHandRaisePermission: (data: { eventCode: string; enable: boolean }) => {
    const { eventCode, enable } = data;

    return api.instances.auth.post<EventStateSchema>(`${EVENT_API_BASE_PATH}/hand-raise/${enable}/${eventCode}`);
  },

  dropHand: (eventCode: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/remove/hand/raise/${eventCode}`),

  declineInviteRequest: (eventCode: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/decline/invite/request/${eventCode}`),

  endEvent: (eventCode: string) =>
    api.instances.auth.delete<EventStateSchema>(`${EVENT_API_BASE_PATH}/end/event/${eventCode}`),

  getEventStats: (eventCode: string) =>
    api.instances.auth.get<EventStatsSchema>(`${EVENT_API_BASE_PATH}/get/stats/${eventCode}`),

  getSubscribedChannels: () =>
    api.instances.auth.get<ChannelSchema[]>(`${USER_API_BASE_PATH}/find/subscribed/channels`),

  createChannel: (data: { name: string; logo?: File }) => {
    const { logo, ...channel } = data;
    const formData = new FormData();

    // SOURCE: https://stackoverflow.com/a/24535293
    formData.append('channel', new Blob([JSON.stringify(channel)], { type: 'application/json' }));

    if (logo) formData.append('logo', logo);

    return api.instances.auth.post<UserSchema>(`${CHANNEL_API_BASE_PATH}/create`, formData);
  },

  getChannelById: (id: string) => api.instances.nonAuth.get<ChannelSchema>(`${CHANNEL_API_BASE_PATH}/${id}`),

  getChannelByName: (name: string) => api.instances.nonAuth.get<ChannelSchema>(`${CHANNEL_API_BASE_PATH}/find/${name}`),

  getChannelEvents: (channelId: string) =>
    api.instances.nonAuth.get<EventSchema[]>(`${CHANNEL_API_BASE_PATH}/find/events/${channelId}`),

  getChannelRecordedPastEvents: (channelId: string, page: number) =>
    api.instances.auth.get<PaginatedResponseSchema<PastEventSchema>>(
      `${CHANNEL_API_BASE_PATH}/find/recorded-events/${channelId}`,
      {
        params: {
          page,
          size: PAGINATION_SIZE
        }
      }
    ),

  getChannelSubscribersCount: (channelId: string) =>
    api.instances.nonAuth.get<number>(`${CHANNEL_API_BASE_PATH}/count/subscribers/${channelId}`),

  getChannelFinishedEventsCount: (channelId: string) =>
    api.instances.nonAuth.get<number>(`${CHANNEL_API_BASE_PATH}/count/done/events/${channelId}`),

  updateChannel: (data: { name?: string; description?: string; logo?: File }) => {
    const { logo, ...channel } = data;
    const formData = new FormData();

    // SOURCE: https://stackoverflow.com/a/24535293
    formData.append('partialUpdates', new Blob([JSON.stringify(channel)], { type: 'application/json' }));

    if (logo) formData.append('logo', logo);

    return api.instances.auth.patch<ChannelSchema>(`${CHANNEL_API_BASE_PATH}/update`, formData);
  },

  subscribeToChannel: (channelId: string) =>
    api.instances.auth.post<{ response: string }>(`${CHANNEL_API_BASE_PATH}/subscribe/${channelId}`),

  unsubscribeFromChannel: (channelId: string) =>
    api.instances.auth.post<{ response: string }>(`${CHANNEL_API_BASE_PATH}/unsubscribe/${channelId}`),

  getUser: () => api.instances.auth.get<UserSchema>(USER_API_BASE_PATH),

  startFreeTrial: (activationCode: string) =>
    api.instances.auth.post<UserSchema>(`${USER_API_BASE_PATH}/start/free/trial/${activationCode}`),

  getPricingPlans: () => api.instances.nonAuth.get<PricingPlanSchema[]>(`${PRICING_PLAN_API_BASE_PATH}/find/all`),

  getPricingPlanById: (id: string) =>
    api.instances.nonAuth.get<PricingPlanSchema>(`${PRICING_PLAN_API_BASE_PATH}/${id}`),

  preparePayment: (pricingPlanId: string) =>
    api.instances.auth.post<string>(`${PAYMENT_API_BASE_PATH}/prepare/${pricingPlanId}/purchase`),

  prepareMembershipPayment: (channelName: string) =>
    api.instances.auth.post<string>(`${PAYMENT_API_BASE_PATH}/prepare/${channelName}/membership-purchase`),

  setMembershipPrice: (channelName: string, price: number) =>
    api.instances.auth.patch<ChannelSchema>(`${CHANNEL_API_BASE_PATH}/set-membership-price`, {
      channelName,
      price
    }),

  togglePastEventVisibility: (eventCode: string, visible: boolean) =>
    api.instances.auth.post(`${EVENT_API_BASE_PATH}/past-event/${!visible}/${eventCode}`)
};

export default requests;
